import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import axios from 'axios';

import PageShell from '../components/PageShell';
import { useLayoutTokens } from '../hooks/useLayoutTokens';
import { getApiErrorMessage } from '../utils/apiError';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface PlannedEvent {
  id: number;
  source: 'calendar_ics' | 'google_calendar';
  source_calendar_id?: string | null;
  title?: string | null;
  description?: string | null;
  location?: string | null;
  start_time: string;
  end_time?: string | null;
  all_day: boolean;
  status: string;
}

const SOURCE_LABELS: Record<PlannedEvent['source'], string> = {
  calendar_ics: 'ICS',
  google_calendar: 'Google Calendar',
};

const formatDateTime = (iso?: string | null): string => {
  if (!iso) return '-';
  return new Date(iso).toLocaleString();
};

const PlannedEvents: React.FC = () => {
  const { pageHeaderSx, sectionGapSx, tableCellPaddingSx } = useLayoutTokens();
  const [events, setEvents] = useState<PlannedEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEvents = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get<PlannedEvent[]>(
        `${API_BASE_URL}/api/planned-events/`,
      );
      setEvents(response.data);
      setError(null);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load planned schedule'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  if (loading) {
    return (
      <PageShell>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Box sx={pageHeaderSx}>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" component="h1">
              Planned Schedule
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Calendar plans imported from ICS files and Google Calendar.
            </Typography>
          </Box>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchEvents}>
            Refresh
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={sectionGapSx} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ '& .MuiTableCell-root': tableCellPaddingSx }}>
          <TableHead>
            <TableRow>
              <TableCell>Source</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Start</TableCell>
              <TableCell>End</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6}>
                  <Typography variant="body2" color="text.secondary">
                    No planned events yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Chip size="small" label={SOURCE_LABELS[event.source]} />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {event.title || 'Untitled'}
                    </Typography>
                    {event.all_day && (
                      <Typography variant="caption" color="text.secondary">
                        All day
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>{event.location || '-'}</TableCell>
                  <TableCell>{formatDateTime(event.start_time)}</TableCell>
                  <TableCell>{formatDateTime(event.end_time)}</TableCell>
                  <TableCell>{event.status}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </PageShell>
  );
};

export default PlannedEvents;
